import { useNavigate } from 'react-router';
import { Tag } from 'antd';
import type { User } from '@viq-admin/types';
import { DataTable, roleLabel, type DataColumn, type TableState } from '@viq-admin/ui';
import { UserLink } from '../../../components/EntityLinks';
import { userHref } from '../../../components/entityHrefs';
import { ROLE_COLOR } from './model';
import { useCompanyUsers } from './useCompanyHubData';

/** Module-level for a stable identity (see DataTable's `defaultState` JSDoc). */
const DEFAULT_STATE: Partial<TableState> = { sort: { field: 'name', order: 'ascend' } };

/**
 * This company's users, active and deactivated. Read-only: invites and
 * lifecycle actions stay on the Users list and the user page; a row opens
 * the user.
 *
 * The table is uncontrolled (no URL state): several tabs on this one route
 * hold tables, and URL-synced sort/search params would collide between them.
 */
export function CompanyUsersTab({ companyId }: { companyId: string }) {
  const navigate = useNavigate();
  const { users, isLoading, error, refetch } = useCompanyUsers(companyId);

  const columns: DataColumn<User>[] = [
    {
      dataIndex: 'name',
      key: 'name',
      title: 'Name',
      kind: 'text',
      width: 200,
      render: (_: unknown, r: User) => <UserLink oid={r.oid} name={r.name} />,
    },
    { dataIndex: 'email', key: 'email', title: 'Email', kind: 'text', width: 260 },
    {
      dataIndex: 'role',
      key: 'role',
      title: 'Role',
      kind: 'enum',
      width: 140,
      render: (v: string) => <Tag color={ROLE_COLOR[v] ?? 'default'}>{roleLabel(v)}</Tag>,
      projectValue: (r) => roleLabel(r.role),
    },
    { dataIndex: 'lastLoginAt', key: 'lastLoginAt', title: 'Last login', kind: 'dateTime', width: 200 },
    {
      dataIndex: 'deactivatedAt',
      key: 'status',
      title: 'Status',
      kind: 'enum',
      width: 100,
      render: (v: string | null) => (v ? <Tag>Inactive</Tag> : <Tag color="green">Active</Tag>),
      projectValue: (r) => (r.deactivatedAt ? 'Inactive' : 'Active'),
    },
  ];

  return (
    <DataTable<User>
      dataSource={users}
      loading={isLoading}
      error={error ?? undefined}
      onRetry={() => void refetch()}
      columns={columns}
      rowKey="oid"
      storageKey="companyHubUsers"
      defaultState={DEFAULT_STATE}
      searchFields={['name', 'email']}
      searchPlaceholder="Search users"
      onRowClick={(r) => navigate(userHref(r.oid))}
      empty={{ title: 'No users yet', description: 'Users invited to this company appear here.' }}
    />
  );
}
